import {Location} from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnInit,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import {NavigationEnd, NavigationStart, Router} from '@angular/router';
import {AlertController, IonRouterOutlet, NavController, Platform} from '@ionic/angular';
import {TranslateService} from '@ngx-translate/core';
import {App, URLOpenListenerEvent} from '@capacitor/app';
import {ActionPerformed, PushNotifications, PushNotificationSchema, Token,} from '@capacitor/push-notifications';
import {NativeAudio} from "@capgo/native-audio";
import {GlobalService} from './services/global/global.service';
import {NotificationsService} from './services/notifications/notifications.service';
import {StorageService} from './services/storage/storage.service';
import {AuthService} from "./services/auth/auth.service";
import {SetMetaService} from "./services/set-meta/set-meta.service";
import {Capacitor} from "@capacitor/core";
import {StatusBar, Style} from '@capacitor/status-bar'
import {NavigationService} from "./services/navigations/navigation.service";
import {keyValModel} from "./models/general.model";
import {UserModel} from "./models/user.model";
import {firstValueFrom} from "rxjs";

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit {
  @ViewChild(IonRouterOutlet) routerOutlet: IonRouterOutlet;
  @ViewChild('tabsContainer') tabsContainer: ElementRef<HTMLElement>;

  get panelWidth(): number {
    return this.tabsContainer?.nativeElement?.offsetWidth ?? 0;
  }

  language: string = '';
  showComponents: boolean = false;
  showTopActionPoints: boolean = false;
  showMyProfile: boolean = false;
  showMyOrganisations: boolean = false;
  isAuthenticated: boolean = false;
  showExploreMoreButton: boolean = false;
  showLoginSide: boolean = false;
  headerType: string = 'global';
  isWeb: boolean = false;
  isMobile: boolean = false;

  isImpactMap: boolean = false;

  active = 'home';

  mapVisible: boolean = true;
  mapWidth: WritableSignal<number> = signal(0);
  searchResultsOpen: boolean = false;

  public menuMapIcons: keyValModel[] = [
    { key: 'home', val: 'home' },
    { key: 'search', val: 'search' },
    { key: 'action-point', val: 'ap' },
    { key: 'leaf', val: 'leaf' },
    { key: 'team', val: 'team' },
    { key: 'profile', val: 'profile' }
  ]

  public topMenuIcons: keyValModel[] = [
    { key: 'notification', val: 'bell' },
    { key: 'creation', val: 'plus' },
  ]

  constructor(
    private translate: TranslateService,
    private router: Router,
    private location: Location,
    private navController: NavController,
    private alertController: AlertController,
    public globalService: GlobalService,
    private notificationService: NotificationsService,
    public platform: Platform,
    private storage: StorageService,
    private ngZone: NgZone,
    private auth: AuthService,
    private setMeta: SetMetaService,
    private navigationService: NavigationService
  ) {
    this.isWeb = !Capacitor.isNativePlatform();
    if (this.globalService.isBrowser) {
      this.isMobile = window.innerWidth < 768;
      const user: UserModel = this.auth.user;
      if (navigator.language.toLocaleLowerCase().includes('de') === true && (!user || user?.token === '') || user?.language === 'de') {
        this.language = localStorage.getItem('selectLanguage') || '';
        if (this.language === '') {
          localStorage.setItem('selectLanguage', 'de');
          this.language = 'de';
        }
      } else {
        this.language = localStorage.getItem('selectLanguage') || '';
        if (this.language === '') {
          localStorage.setItem('selectLanguage', 'en');
          this.language = 'en';
        }
      }
    } else {
      this.language = 'en';
    }

    if (Capacitor.isNativePlatform()) {
      const setStatusBar = async () => {
        await StatusBar.setOverlaysWebView({overlay: false});
        await StatusBar.setBackgroundColor({color: '#ffffffff'});
        await StatusBar.setStyle({style: Style.Light});
      };

      setStatusBar();
    }

    this.translate.use(this.language);
    this.globalService.languageEvent.next(this.language);
    this.initializeApp();
  }


  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    const width = event.target.innerWidth;
    // Automatically hide tabs if screen is too small
    this.mapVisible = width > this.globalService.breakPointMobile;

    const tabWidth: number =  this.tabsContainer?.nativeElement?.offsetWidth ?? 0;
    this.mapWidth.set(window.innerWidth - tabWidth);
  }

  ngAfterViewInit() {
    const tabWidth: number =  this.tabsContainer?.nativeElement?.offsetWidth ?? 0;
    if(this.globalService.isBrowser){
      this.mapWidth.set(window.innerWidth - tabWidth);
    }
  }

  async ngOnInit() {
    this.setMeta.setMetaData('', '', '');
    if (this.globalService.isBrowser) {
      this.showComponents = window.innerWidth > this.globalService.breakPointShowComponents;
      window.addEventListener('resize', () => {
        this.showComponents = window.innerWidth > this.globalService.breakPointShowComponents;
      });

      this.onResize({target: window});
    } else {
      this.showComponents = false;
    }

    if(this.auth && this.auth.user && this.auth.user.isCMSAdmin){
      this.topMenuIcons.push({ key: 'dashboard', val: 'dashboard' });
    }

    this.router.events.subscribe(async (e) => {
      if (e instanceof NavigationStart) {
        this.globalService.lastRoute = this.router.url;
      }
      if (e instanceof NavigationEnd) {
        this.isAuthenticated = this.auth.isAuthenticated;
        this.showExploreMoreButton = this.router.url.startsWith('/anonymous/')
        this.showTopActionPoints = this.router.url !== '/app/action-point' && this.router.url !== '/login' && this.router.url !== '/register' && !this.showExploreMoreButton;
        this.showMyProfile = this.router.url !== '/app/profile';
        this.showMyOrganisations = this.router.url !== '/app/team';
        this.showLoginSide = !this.auth.isAuthenticated && this.router.url !== '/login' && this.router.url !== '/register' && !this.showExploreMoreButton;
        this.headerType = this.router.url.startsWith('/app/team/view-team') ?
          'team' : this.router.url.startsWith('/app/team/company/view-company') ? 'company' : 'global';

        this.isImpactMap = this.router.url.startsWith('/app/impact-map');

        this.active = this.navigationService.setCurrentTab();
      }
    });

    await this.platform.ready();

    if (Capacitor.isNativePlatform() && this.globalService.isBrowser) {

      // App opened by URL
      await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        this.ngZone.run(async () => {
          const slug = event.url.split('://')
          if (slug) {
            if (slug.length > 1 && slug[1].includes('qr')) {
              const url = slug[1].split('/');
              const codeId = url[url.length - 1];
              await this.router.navigateByUrl(`/qr/${codeId}`);
            } else if (slug.length > 1 && slug[1].includes('ap-img')) {
              const url = slug[1].split('/');
              const codeId = url[url.length - 1];
              await this.router.navigateByUrl(`/ap-img/${codeId}`);
            } else if (slug.length > 1 && slug[1].includes('share')) {
              const url = slug[1].split('/');
              const postId = url[url.length - 1];
              await this.router.navigateByUrl(`/share/${postId}`);
            } else {
              await this.router.navigate([''])
            }
          }
        });
      });
      // Request permission to use push notifications
      // iOS will prompt the user and return if they granted permission or not.
      // Android will just grant permission without prompting
      const permStatus = await PushNotifications.checkPermissions();
      if (permStatus.receive === 'granted') {
        await PushNotifications.register();
      } else {
        await PushNotifications.requestPermissions();
      }
      PushNotifications.requestPermissions().then(result => {
        if (result.receive === 'granted') {
          // Register with Apple / Google to receive push via APNS/FCM
          PushNotifications.register();
        } else {
          // Show some error
          console.log('ERROR PushNotification');
        }
      });

      // On success, we should be able to receive notifications
      await PushNotifications.addListener('registration',
        (token: Token) => {
          localStorage.setItem('deviceToken', token.value);
        }
      );

      // Some issue with our setup and push will not work
      await PushNotifications.addListener('registrationError',
        (error: any) => {
          console.error('Error on registration: ' + JSON.stringify(error));
        }
      );

      // Show us the notification payload if the app is open on our device
      await PushNotifications.addListener('pushNotificationReceived',
        (notification: PushNotificationSchema) => {
          console.error('Push received: ' + JSON.stringify(notification));
        }
      );

      // Method called when tapping on a notification
      await PushNotifications.addListener('pushNotificationActionPerformed',
        (notification: ActionPerformed) => {
          this.openNotification(notification);
        }
      );


      try {
        await NativeAudio.preload({
          assetId: 'like',
          assetPath: 'assets/sounds/like.wav',
          volume: 1.0,
        });
      } catch (e) {
        console.log('Error preload Audio Like', e);
      }

      try {
        await NativeAudio.preload({
          assetId: 'adopt',
          assetPath: 'assets/sounds/adopt.wav',
          volume: 1.0,
        });
      } catch (e) {
        console.log('Error preload Audio Adopt', e);
      }

      try {
        await NativeAudio.preload({
          assetId: 'impact',
          assetPath: 'assets/sounds/impact.wav',
          volume: 1.0,
        });
      } catch (e) {
        console.log('Error preload Audio impact', e);
      }

      try {
        await NativeAudio.preload({
          assetId: 'impact2',
          assetPath: 'assets/sounds/impact2.wav',
          volume: 1.0,
        });
      } catch (e) {
        console.log('Error preload Audio Impact2', e);
      }

      try {
        await NativeAudio.preload({
          assetId: 'moment',
          assetPath: 'assets/sounds/moment.wav',
          volume: 1.0,
        });
      } catch (e) {
        console.log('Error preload Audio Moment', e);
      }
    }
    await this.storage.checkForCreatedStorage();
  }

  openNotification(res: any): void {
    // whenever the user clicks on notification app jump to corresponding page
    if (res.notification) {
      if (res.notification.data.target === 'Action Point' || res.notification.data.target === 'Adopted Action Point') {
        this.navController.navigateForward(['/app/home/view-post/action_point/' + res.notification.data.targetId + '/detail']);
      }
      if (res.notification.data.target === 'Post') {
        this.navController.navigateForward(['/app/home/view-post/post/' + res.notification.data.targetId + '/detail']);
      }
      if (res.notification.data.target === 'User') {
        this.navController.navigateForward(['/app/home/other-profile/' + res.notification.data.targetId]);
      }
      if (res.notification.data.target === 'Company') {
        this.navController.navigateForward(['/app/team/company/other-company/' + res.notification.data.targetId]);
      }
      if (res.notification.data.target === 'Team') {
        this.navController.navigateForward(['/app/team/other-team/' + res.notification.data.targetId]);
      }
      if (res.notification.data.target === 'Story') {
        this.notificationService.scrollTopWhenStoryEvent.next('Scroll');
      }
    }
  }

  initializeApp() {
    // handling hardware back button
    this.platform.backButton.subscribeWithPriority(0, () => {
      if (this.routerOutlet && this.routerOutlet.canGoBack()) {
        this.routerOutlet.pop();
      } else if (this.router.url === '/app/home') {
        this.confirmExitApp();
      } else if (this.router.url === '/app/profile/edit-profile') {
        // For this case handling in edit the profile page
      } else {
        this.location.back();
      }
    });

  }

  async confirmExitApp() {
    // alert when there is no back step, close app alert will show
    const alert = await this.alertController.create({
      header: this.translate.instant('hdl_exit_app'),
      message: this.translate.instant('txt_exit_app'),
      buttons: [
        {
          text: this.translate.instant('lbl_yes'),
          id: 'confirm-button',
          handler: () => {
            App.exitApp();
          }
        },
        {
          text: this.translate.instant('lbl_no'),
          role: 'cancel',
          cssClass: 'cancel-button',
          id: 'cancel-button',
        }
      ]
    });
    await alert.present();
  }



  async setTab(tab: string) {
    if(!this.globalService.sidePanelOpen()){
      this.toggleTabs();
    }
    let route: string = this.active = tab;
    if(this.active === 'search'){
      route = 'home/search/all'
    }else if(this.active === 'creation'){
      const val = await firstValueFrom(this.globalService.postactionsurveyEvent);
      const id: number | string = val !== undefined && val !== null && val.id ? val.id : 0;
      const type: string = this.router.url.includes('view-team') ? 'team' : 'global';
      route = `home/post-modal/${type}/${id}`
    }else if(this.active === 'notification'){
      route = 'home/notifications'
    }else if(this.active === 'dashboard'){
      this.active = 'home';
      route = 'home';
      this.navigationService.openImpactSuite();
    }
    await this.router.navigate([`/app/${route}`]);
  }

  toggleTabs() {
    this.globalService.sidePanelOpen.set(!this.globalService.sidePanelOpen())
  }
}
